import React from 'react';
import {
   chain,
   filter,
   includes,
   isEmpty,
   isNil,
   isUndefined,
   map,
   findKey,
} from 'lodash';
import { getClonedObject } from '../../../utils/common-util.js';
import { FO_HOST_CARRIER_KEY } from '../../../utils/constants.js';

export const OW = 1;
export const RT = 2;
export const OWNC = 3;

export const getUserId = (appConfigs) => {
   return appConfigs.userId;
};

export const getAnalystApproversList = (appConfigs) => {
   const analystApproverData = getClonedObject(appConfigs.foAnalystApprover);
   let allApprovers = ['AVFO'];
   map(analystApproverData, (eachAnalystApporerData) => {
      map(
         eachAnalystApporerData.properties.foAnalystPosMappingProperties,
         (eachAnalystCollection) => {
            map(eachAnalystCollection.approversList, (approversList) => {
               allApprovers = [
                  ...allApprovers,
                  ...approversList.userName.split(','),
               ];
            });
         }
      );
   });

   return allApprovers;
};

export const getTransformedAnalystApproversList = (appConfigs) => {
   const analystApproverList = [];
   const allApprovers = getAnalystApproversList(appConfigs);
   map(allApprovers, (eachApprovers) => {
      analystApproverList.push({
         label: eachApprovers,
         value: eachApprovers,
      });
   });

   return getUniqueSortedValues(analystApproverList);
};

export const getFareBrandsForHostCarrier = (appConfigs) => {
   return getClonedObject(appConfigs.fareBrandDefinitionProperties);
};

export const getTransformedFareBrandsForHostCarrierList = (appConfigs) => {
   const fareBrandsByCarrier = getFareBrandsForHostCarrier(appConfigs) || [];
   if (!isNil(sessionStorage.getItem('carrierCode'))) {
      const hostCarrier = sessionStorage.getItem('carrierCode').split(',');
      return transform(fareBrandsByCarrier, function (result, current) {
         if (includes(hostCarrier, current.carrier)) {
            result.push(
               ...getLabelValuePair(
                  chain(current.fareBrands).map('fareBrand').value()
               )
            );
         }
      });
   }
};

export const getAdvancedPurchaseGrouping = (appConfigs) => {
   return getClonedObject(appConfigs.FOAdvancePurchaseGrouping);
};

export const getTransformedAdvancedPurchaseGroupingList = (appConfigs) => {
   let inputMaxStayGroups = getAdvancedPurchaseGrouping(appConfigs);
   inputMaxStayGroups = transformGroups(
      inputMaxStayGroups,
      'advancePurchaseStart',
      'advancePurchaseEnd'
   );
   const advancedPurchaseGroupsListValues = [
      { label: '-', value: '-' },
      { label: 'None', value: 'None' },
      ...getLabelValuePair(inputMaxStayGroups),
   ];
   return getUniqueValues(advancedPurchaseGroupsListValues);
};

export const getBenchmarkCarriers = (appConfigs) => {
   return getClonedObject(appConfigs.foBenchmarkCarriers);
};

export const getTransformedBenchmarkCarriersList = (
   appConfigs,
   isHostCarrierToBeIncluded = false
) => {
   const benchmarkCarrierListValues = [];
   const inputBenchmarkCarriersData = getBenchmarkCarriers(appConfigs);
   map(inputBenchmarkCarriersData, (eachBenchmarkCarrier) => {
      benchmarkCarrierListValues.push({
         label: eachBenchmarkCarrier.benchmarkCarriers[0],
         value: eachBenchmarkCarrier.benchmarkCarriers[0],
      });
   });
   if (isHostCarrierToBeIncluded) {
      const hostCarrier = getSimpleHepProperty(appConfigs, FO_HOST_CARRIER_KEY);
      benchmarkCarrierListValues.push({
         label: hostCarrier,
         value: hostCarrier,
      });
   }
   return getUniqueSortedValues(benchmarkCarrierListValues);
};

export const getMaxStayGroups = (appConfigs) => {
   return getClonedObject(appConfigs.FOMaxStayGrouping) || [];
};

export const getTransformedMaxStayGroupsList = (appConfigs) => {
   let inputMaxStayGroups = getMaxStayGroups(appConfigs);
   inputMaxStayGroups = transformGroups(
      inputMaxStayGroups,
      'maxStayStart',
      'maxStayEnd'
   );
   const maxStayGroupListValues = [
      { label: '-', value: '-' },
      { label: 'None', value: 'None' },
      ...getLabelValuePair(inputMaxStayGroups),
   ];
   return getUniqueValues(maxStayGroupListValues);
};

export const getMinStayGroups = (appConfigs) => {
   return getClonedObject(appConfigs.FOMinStayGrouping) || [];
};

export const getTransformedMinStayGroupsList = (appConfigs) => {
   let inputMinStayGroups = getMinStayGroups(appConfigs);
   inputMinStayGroups = transformGroups(
      inputMinStayGroups,
      'minStayStart',
      'minStayEnd'
   );
   const minStayGroupListValues = [
      { label: '-', value: '-' },
      { label: 'None', value: 'None' },
      ...getLabelValuePair(inputMinStayGroups),
   ];
   return getUniqueValues(minStayGroupListValues);
};

export const getFareType = (fareType) => {
   const fareTypes = { [OW]: 'OW', [RT]: 'RT', [OWNC]: 'OWNC' };

   if (isEmpty(fareType)) {
      return fareTypes;
   } else {
      return parseInt(findKey(fareTypes, (value) => value === fareType));
   }
};

export const getTransformedFareTypeList = () => {
   const inputFareType = getFareType();
   const fareTypeListValues = getLabelValuePair(inputFareType);
   return getUniqueSortedValues(fareTypeListValues);
};

export const getCabins = (appConfigs) => {
   return getClonedObject(appConfigs.foCabins);
};

export const getTransformedCabinList = (appConfigs) => {
   const inputCabinData = getCabins(appConfigs);
   const cabinListValues = getLabelValuePair(inputCabinData);
   return getUniqueSortedValues(cabinListValues);
};

export const getMarkets = (appConfigs) => {
   return getClonedObject(appConfigs.foMarkets);
};

export const getTransformedMarketDestinationList = (appConfigs) => {
   const inputOriginData = getMarkets(appConfigs);
   const destinationListValues = getLabelValuePair(
      inputOriginData,
      'destination'
   );
   return getUniqueSortedValues(destinationListValues);
};

export const getTransformedMarketOriginList = (appConfigs) => {
   const inputOriginData = getMarkets(appConfigs);
   const originListValues = getLabelValuePair(inputOriginData, 'origin');
   return getUniqueSortedValues(originListValues);
};

export const getTransformedMarketList = (appConfigs) => {
   const inputMarketData = getMarkets(appConfigs);
   const marketListValues = getLabelValuePair(inputMarketData, 'market');
   return getUniqueSortedValues(marketListValues);
};

export const getMarketTiers = (appConfigs) => {
   return getClonedObject(appConfigs.marketTiers);
};

export const getSimpleHepProperty = (appConfigs, property) => {
   return appConfigs.foSimpleHep[property];
};

export const getFeaturesHepProperty = (appConfigs, property) => {
   const propertyDetails = filter(appConfigs.features, { feature: property });
   return !isEmpty(propertyDetails) ? propertyDetails[0] : {};
};

export const getTransformedMarketTierList = (appConfigs) => {
   const marketTiersListValues = [];
   const inputMarketTiersData = getMarketTiers(appConfigs);
   map(inputMarketTiersData, (eachMarketTier) => {
      marketTiersListValues.push({
         label: !(
            isUndefined(eachMarketTier.marketTier) ||
            isNil(eachMarketTier.marketTier)
         )
            ? eachMarketTier.marketTier
            : 'Not assigned',
         value: !(
            isUndefined(eachMarketTier.marketTier) ||
            isNil(eachMarketTier.marketTier)
         )
            ? eachMarketTier.marketTier
            : 'Not assigned',
      });
   });

   return getUniqueSortedValues(marketTiersListValues);
};

export const getPOSes = (appConfigs) => {
   return getClonedObject(appConfigs.foPoses);
};

export const getTransformedPOSList = (appConfigs) => {
   const inputPOSData = getPOSes(appConfigs);
   const posListValues = getLabelValuePair(inputPOSData);
   return getUniqueSortedValues(posListValues);
};

export const getRbds = (appConfigs) => {
   return getClonedObject(appConfigs.foRbds);
};

export const getTransformedRbdsList = (appConfigs) => {
   const inputRbdsData = getRbds(appConfigs);
   const rbdsListValues = getLabelValuePair(inputRbdsData);
   return getUniqueSortedValues(rbdsListValues);
};

export const getRegions = (appConfigs) => {
   return getClonedObject(appConfigs.foRegions);
};

export const getTransformedRegionsList = (appConfigs) => {
   const inputRegionsData = getRegions(appConfigs);
   const marketListValues = getLabelValuePair(inputRegionsData);
   return getUniqueSortedValues(marketListValues);
};

export const getSeasons = (
   appConfigs,
   isConcatAbbreviation,
   isShowNoSeason
) => {
   const inputSeasonsData = getClonedObject(appConfigs.seasonDefinitions);
   if (isShowNoSeason) {
      inputSeasonsData.push({ seasonAbbreviation: '-', season: 'No Season' });
   }
   const seasonDetails = {};
   map(inputSeasonsData, (details) => {
      seasonDetails[details.seasonAbbreviation] = isConcatAbbreviation
         ? `${details.seasonAbbreviation} (${details.season})`
         : details.season;
   });

   return seasonDetails;
};

export const getTransformedSeasonsList = (
   appConfigs,
   { isConcatAbbreviation = false, isShowNoSeason = false }
) => {
   const inputSeasonsData = getSeasons(
      appConfigs,
      isConcatAbbreviation,
      isShowNoSeason
   );
   const seasonListValues = getLabelValuePair(inputSeasonsData, '', true);
   return getUniqueSortedValues(seasonListValues);
};

export const getLabelValuePair = (
   dataSet,
   key,
   isKeyValuePair = false,
   isFirstEmpty = false
) => {
   const listValues = isFirstEmpty ? [{ label: '', value: '' }] : [];
   map(dataSet, (eachDataSet, eachDataSetKey) => {
      const labelValue =
         key === 'market'
            ? `${eachDataSet.origin}-${eachDataSet.destination}`
            : isEmpty(key)
            ? eachDataSet
            : eachDataSet[key];
      listValues.push({
         label: labelValue,
         value: isKeyValuePair ? eachDataSetKey : labelValue,
      });
   });

   return listValues;
};

const transformGroups = (groups, startKey, endKey) =>
   map(groups, (group) => `${group[startKey]} - ${group[endKey]}`);

export const getUniqueSortedValues = (objectList) =>
   chain(objectList).uniqBy('label').sortBy('label').value();

const getUniqueValues = (objectList) =>
   chain(objectList).uniqBy('label').value();
